<template>
  <section class="panel">
    <div class="panel-header">
      <div class="panel-header-left">
        <h2>Device Snitch</h2>

        <button
          class="header-filter-btn"
          :class="{ 'header-filter-btn--active': showOfflineOnly }"
          @click="toggleOfflineOnly"
        >
          {{ showOfflineOnly ? "Showing Offline" : "Offline Only" }}
        </button>
      </div>

      <div class="panel-meta">{{ filteredDevices.length }} devices</div>
    </div>

    <div
      class="panel-body"
      ref="scrollContainer"
      @mouseenter="pauseAutoScroll"
      @mouseleave="resumeAutoScroll"
    >
      <table class="snitch-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Last Device Seen</th>
            <th>Last Sync Attempt</th>
            <th>Last Sync Success</th>
            <th>Last Boot</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="device in filteredDevices" :key="device">
            <td class="device-name">{{ device }}</td>

            <td>
              <span class="status-wrap">
                <span
                  class="status-dot"
                  :class="getSeenStatusClass(sortedSnitches[device].last_seen)"
                ></span>
                <span>{{
                  parseTimestamp(sortedSnitches[device].last_seen)
                }}</span>
              </span>
            </td>

            <td>
              <span class="status-wrap">
                <span
                  class="status-dot"
                  :class="
                    getSyncAttemptStatusClass(
                      sortedSnitches[device].last_sync_attempt
                    )
                  "
                ></span>
                <span>{{
                  parseTimestamp(sortedSnitches[device].last_sync_attempt)
                }}</span>
              </span>
            </td>

            <td>
              <span class="status-wrap">
                <span
                  class="status-dot"
                  :class="
                    getSyncSuccessStatusClass(
                      sortedSnitches[device].last_sync_success
                    )
                  "
                ></span>
                <span>{{
                  parseTimestamp(sortedSnitches[device].last_sync_success)
                }}</span>
              </span>
            </td>

            <td>
              <span class="status-wrap">
                <span
                  class="status-dot"
                  :class="getBootStatusClass(sortedSnitches[device].last_boot)"
                ></span>
                <span>{{
                  parseTimestamp(sortedSnitches[device].last_boot)
                }}</span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { setMonitorAlerts } from "@/store/monitorAlerts";

export default {
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      snitches: {},
      refreshInterval: null,
      autoScrollInterval: null,
      autoScrollPaused: false,
      autoScrollStep: 1,
      autoScrollDelay: 40,
      showOfflineOnly: false,
    };
  },

  computed: {
    normalizedSearch() {
      return this.searchQuery.trim().toLowerCase();
    },

    sortedSnitches() {
      const normalizedSnitches = {};

      Object.keys(this.snitches).forEach(snitch => {
        const normalizedSnitch = snitch
          .toLowerCase()
          .replace(".local", "")
          .trim();

        normalizedSnitches[normalizedSnitch] =
          normalizedSnitches[normalizedSnitch] || {};

        Object.assign(
          normalizedSnitches[normalizedSnitch],
          this.snitches[snitch]
        );
      });

      return Object.keys(normalizedSnitches)
        .sort()
        .reduce((result, key) => {
          result[key] = normalizedSnitches[key];
          return result;
        }, {});
    },

    devices() {
      return Object.keys(this.sortedSnitches);
    },

    filteredDevices() {
      let devices = this.devices;

      if (this.normalizedSearch) {
        devices = devices.filter(device =>
          device.toLowerCase().includes(this.normalizedSearch)
        );
      }

      if (this.showOfflineOnly) {
        devices = devices.filter(device => {
          const item = this.sortedSnitches[device];
          return this.getSeenStatusClass(item && item.last_seen) === "offline";
        });
      }

      return devices;
    },
  },

  mounted() {
    this.getSnitches();

    this.refreshInterval = setInterval(() => {
      this.getSnitches();
    }, 5000);

    this.startAutoScroll();
  },

  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    if (this.autoScrollInterval) clearInterval(this.autoScrollInterval);
  },

  methods: {
    startAutoScroll() {
      if (this.autoScrollInterval) clearInterval(this.autoScrollInterval);

      this.autoScrollInterval = setInterval(() => {
        if (this.autoScrollPaused) return;

        const el = this.$refs.scrollContainer;
        if (!el) return;

        const bottom = el.scrollHeight - el.clientHeight;

        if (bottom <= 0) return;

        const nextScrollTop = el.scrollTop + this.autoScrollStep;
        const threshold = 2;

        if (nextScrollTop >= bottom - threshold) {
          el.scrollTop = 0;
        } else {
          el.scrollTop = nextScrollTop;
        }
      }, this.autoScrollDelay);
    },

    pauseAutoScroll() {
      this.autoScrollPaused = true;
    },

    resumeAutoScroll() {
      this.autoScrollPaused = false;
    },

    getSnitches() {
      axios
        .get(
          process.env.VUE_APP_SNITCH_SERVER
            ? `${process.env.VUE_APP_SNITCH_SERVER}/api/snitches`
            : "/api/snitches"
        )
        .then(res => {
          this.snitches = res.data;
          this.publishSnitchAlerts();
        });
    },

    parseTimestamp(ms) {
      if (!ms) return "-";

      const now = new Date().getTime();
      const difference = now - ms;
      const minutes = Math.round(difference / 1000 / 60);

      if (minutes < 1) return "< 1m ago";
      if (minutes < 60) return minutes + "m ago";
      if (minutes < 1440) {
        return Math.floor(minutes / 60) + "hr " + (minutes % 60) + "m ago";
      }

      return (
        Math.floor(minutes / 1440) +
        "d " +
        Math.floor((minutes % 1440) / 60) +
        "hr " +
        (minutes % 60) +
        "m ago"
      );
    },

    getSeenStatusClass(ms) {
      if (!ms) return "offline";

      const now = new Date().getTime();
      const difference = now - ms;
      const minutes = Math.round(difference / 1000 / 60);

      if (minutes > 60 * 24) return "offline";
      if (minutes > 1) return "warning";
      return "online";
    },

    getSyncAttemptStatusClass(ms) {
      if (!ms) return "offline";

      const now = new Date().getTime();
      const difference = now - ms;
      const minutes = Math.round(difference / 1000 / 60);

      if (minutes > 10) return "offline";
      if (minutes > 5) return "warning";
      return "online";
    },

    getSyncSuccessStatusClass(ms) {
      if (!ms) return "offline";

      const now = new Date().getTime();
      const difference = now - ms;
      const minutes = Math.round(difference / 1000 / 60);

      if (minutes > 60 * 24 * 14) return "offline";
      if (minutes > 60 * 24 * 7) return "warning";
      return "online";
    },

    getBootStatusClass(ms) {
      if (!ms) return "offline";

      const now = new Date().getTime();
      const difference = now - ms;
      const minutes = Math.round(difference / 1000 / 60);

      if (minutes > 60 * 24) return "offline";
      return "online";
    },

    publishSnitchAlerts() {
      const devicesOffline = this.devices.filter(device => {
        const item = this.sortedSnitches[device];
        return this.getSeenStatusClass(item && item.last_seen) === "offline";
      }).length;

      setMonitorAlerts({
        devicesOffline,
      });
    },

    toggleOfflineOnly() {
      this.showOfflineOnly = !this.showOfflineOnly;
    },
  },
};
</script>

<style scoped>
.panel {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #0b1622;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.panel-header {
  flex-shrink: 0;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #f1f5f9;
}

.panel-meta {
  color: #94a3b8;
  font-size: 0.82rem;
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.panel-body::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.panel-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.14);
  border-radius: 999px;
}

.snitch-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.snitch-table th {
  text-align: left;
  padding: 10px 12px;
  color: #94a3b8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: sticky;
  top: 0;
  background: #0b1622;
  z-index: 1;
  white-space: nowrap;
}

.snitch-table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #e2e8f0;
  white-space: nowrap;
}

.device-name {
  font-weight: 600;
  color: #f8fafc;
}

.status-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
}

.status-dot.online {
  background: #22c55e;
}

.status-dot.warning {
  background: #f59e0b;
}

.status-dot.offline {
  background: #ef4444;
}

.status-text {
  color: #cbd5e1;
  font-size: 12px;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
}

.header-filter-btn {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd5e1;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease,
    transform 0.18s ease;
}

.header-filter-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: #f8fafc;
}

.header-filter-btn--active {
  background: rgba(239, 68, 68, 0.14);
  border-color: rgba(239, 68, 68, 0.4);
  color: #fca5a5;
}
</style>
